import LinkButton from "components/Common/Link/LinkButton";
import {
	CharacterCountError,
	CreateQuoteHeaderContainer,
	CreateQuoteHeaderHeadContainer,
	CreateQuotePageContainer,
	DesignerCustomizationDesignerRequestSKUSubtitle,
	DesignerCustomizationDesignerRequestSKUTitle,
	DesignerCustomizationDetailsFormContainer,
	DesignerCustomizationDetailsFormContentContainer,
	DesignerCustomizationDetailsFormHeader,
	DesignerInfoDesignerContainer,
	DesignerNumberContainer,
	DesignerRequestBaseSKUContainer,
	DesignerRequestFormAccountAndBillToContainer,
	DesignerRequestFormContainer,
	DesignerRequestSKUContainer,
	ErrorHelperTextContainer,
	ErrorMessageContainer,
	LinkContainer,
	LinkTitle,
	OrderDetailsFormContainer,
	OrderDetailsFormHeader,
	RequiredFieldText,
	ResourceLink,
	RightAlignedFormHelperTextProps,
	SimpleErrorContainer,
	SubmitButtonContainer,
	TopBarContainer
} from "./CreateQuotePage.styles";
import { Button, FormControl, FormHelperText, TextField } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { defaultQuoteRequestFields, QuoteRequestFields, schema } from "./schema";
import AutoComplete from "components/Common/Autocomplete/Autocomplete";
import { DesignerIsNotHereText, SelectAccountNumber, SelectBillToNumber } from "components/NewOrders/constants";
import {
	findAccount,
	findBillTo,
	getAttributes,
	getSubheaderAttributes,
	sortedAccounts,
	sortedBillTos
} from "components/NewOrders/utils/NewOrderUtils";
import { convertToTitleCase } from "utils/string";
import { models } from "types/api/viewModels";
import { brandCheck, displayDesigner } from "utils/order";
import {
	BaseItemHeaderText,
	CustomItemNameLabel,
	CustomizationDetailsHeaderText,
	DescribeCustomizationLabel,
	DesignerNotFoundContactSalesRepText,
	DesignerNumberSixDigitsStartWithOneText,
	EnterDesignerNumberLabel,
	EnterQuote2020Text,
	FieldError,
	GeneralFAQText,
	HelpfulResourcesTitle,
	OrderDetailsHeaderText,
	RequestCustomQuoteHeaderText,
	RequiredFieldsText,
	SelectDesignerNumberLabel,
	StartOver,
	SubmitCustomQuoteRequestButtonText
} from "constants/text";
import { useSelector } from "react-redux";
import { RootState } from "stores/application.store";
import useNewOrderDesigners from "hooks/useNewOrderDesigners";
import { AccountBillToLogo, FileContentsLogo, SummaryProductLine } from "components/NewOrders/NewOrders.styles";
import AddLineItem from "components/NewOrders/AddLineItem/AddLineItem";
import { yupResolver } from "@hookform/resolvers/yup";
import SketchUpload from "components/SketchUpload/SketchUpload";
import useBlobStorage from "hooks/useBlobStorage.ts";
import { v4 as uuidv4 } from "uuid";
import { usePostCustomQuoteMutation } from "features/api/customQuotesApi.ts";
import CreateQuoteFormSkeleton from "components/CustomQuote/CustomQuoteFormSkeleton/CreateQuoteFormSkeleton";
import { useNavigate } from "react-router";
import { uploadFileToBlob } from "utils/blob";
import { useDialog } from "hooks/useDialog.ts";
import { DialogTracking } from "components/Dialog/components/DialogTracking.ts";
import { sanitizeBlobName } from "utils/file";
import { designerOptionsWithCustomDesignerText } from "utils/designer.ts";
import SupportCTA from "components/Common/SupportCTA/SupportCTA";
import { getSupportFormUrl } from "utils/supportForm";
import { Page } from "components/Common/Page";

const CreateQuotePage = () => {
	const navigate = useNavigate();
	const newOrderDetails = useSelector((state: RootState) => state.newOrder.newOrderDetails || null);
	const configuration = newOrderDetails?.configurations?.[0] || null;
	const supportFormUrl = getSupportFormUrl({
		type: "Custom Quote",
		brand: configuration?.globals?.brand?.description,
		poNumber: newOrderDetails?.poNumber
	});
	const productLineAccountInfo = useSelector((state: RootState) => state.newOrder.productLineAccounts);
	const param = configuration?.globals?.productLine?.code ?? "";

	const accounts = productLineAccountInfo[param];
	const configAccount = useMemo(
		() => findAccount(accounts, configuration?.accountNumber),
		[accounts, configuration?.accountNumber]
	);
	const configBillTo = useMemo(
		() => findBillTo(configAccount, configuration?.billToId),
		[configAccount, configuration?.billToId]
	);

	const { designerOptions } = useNewOrderDesigners();
	const { uploadContainerBlob } = useBlobStorage();
	const { openDialog } = useDialog();
	const [postCustomQuote, { isLoading: isSubmitting, error: submitError }] = usePostCustomQuoteMutation();

	const { control, getValues, watch, setValue, clearErrors, trigger } = useForm<QuoteRequestFields>({
		mode: "onChange",
		defaultValues: defaultQuoteRequestFields,
		resolver: yupResolver(schema)
	});

	const [isCustomDesigner, setIsCustomDesigner] = useState(false);
	const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
	const selectedEditItem = useRef<models["PendingLineItemViewModel"] | undefined>(undefined);

	const selectedAccount = watch("accountNumber");
	const designer = watch("designer");

	useEffect(() => {
		if (configAccount) setValue("accountNumber", configAccount);
		if (configBillTo) setValue("billToNumber", configBillTo);
	}, [configAccount, configBillTo, setValue]);

	useEffect(() => {
		if (designer !== DesignerIsNotHereText) {
			clearErrors("customDesigner");
		}
	}, [designer, clearErrors]);

	const handleDesignerChange = (value: models["DesignerViewModel"] | string | null) => {
		setIsCustomDesigner(value === DesignerIsNotHereText);
		if (value !== DesignerIsNotHereText) {
			setValue("customDesigner", "");
		}
	};

	const uploadCustomQuoteFiles = async (requestId: string, files: File[]): Promise<string[]> => {
		const uploadedFiles: string[] = [];

		for (let i = 0; i < files.length; i++) {
			try {
				const file = files[i];
				if (!file) continue;
				const sanitizedFileName = sanitizeBlobName(file.name);
				const fileName = `${i + 1}/${sanitizedFileName}`;
				const fileUrl = URL.createObjectURL(file);
				await uploadFileToBlob({
					fileUrl,
					fileName,
					size: file.size,
					requestId,
					uploadContainerBlob
				});
				uploadedFiles.push(fileName);
			} catch (error) {
				console.error("File upload failed:", error);
			}
		}
		return uploadedFiles;
	};

	const onSubmit = async (_configurationId: string, lineItem: models["LineItemViewModel"]) => {
		const parentFormValid = await trigger();
		if (parentFormValid) {
			const formValues = getValues();
			const requestId = uuidv4();
			const newModifications: models["PendingModificationViewModel"][] =
				lineItem.modifications?.map((modification, index) => ({
					...modification,
					lineItemNumber: Number(`${lineItem.lineItemNumber}.${index + 1}`)
				})) ?? [];

			const newLineItem: models["PendingLineItemViewModel"] = {
				...lineItem,
				modifications: newModifications
			};

			const attachmentUrls =
				uploadedFiles.length > 0 ? await uploadCustomQuoteFiles(requestId, uploadedFiles) : [];

			let designerName = null;
			if (typeof formValues.designer === "string") {
				designerName = formValues.customDesigner || null;
			} else if (formValues.designer) {
				designerName = displayDesigner(formValues.designer);
			}

			const apiPayload = {
				globals: configuration?.globals,
				accountId: formValues.accountNumber?.accountId || "",
				billToId: formValues.billToNumber?.billToId || "",
				lineItem: newLineItem,
				customizationDescription: formValues.customizationDetails || "",
				attachmentUrls: attachmentUrls,
				designerName: designerName,
				quoteId: requestId,
				designerId:
					typeof formValues.designer === "string"
						? formValues.customDesigner || ""
						: formValues.designer?.designerId || "",
				requestName: formValues.customItemName || ""
			};

			try {
				const response = await postCustomQuote(apiPayload).unwrap();
				navigate(`/custom-quote/confirmation/${response?.quoteId}`);
			} catch (error: any) {
				console.error("Failed to submit custom quote request:", error);
			}
		}
	};

	const MAX_DETAILS_LENGTH = 500;
	const MAX_ITEM_NAME_LENGTH = 25;

	const handleFilesSelected = (files: File[]) => {
		setUploadedFiles(files);
	};

	return (
		<Page variant="orders">
			<CreateQuoteHeaderContainer
				variant="panel"
				data-testid="create-quote-header"
			>
				<LinkButton
					hasBackIcon
					onClick={() => {
						openDialog({
							dialogStatus: DialogTracking.LEAVE_CREATE_CUSTOM_QUOTE,
							dialogLeavePath: "/custom-quote"
						});
					}}
					data-testid="create-custom-quote-start-over-link"
				>
					{StartOver}
				</LinkButton>
				<CreateQuoteHeaderHeadContainer>
					<h4>{RequestCustomQuoteHeaderText}</h4>
					<SupportCTA
						href={supportFormUrl}
						data-testid="create-custom-quote-support-button"
						sx={{ height: 30 }}
					/>
				</CreateQuoteHeaderHeadContainer>
			</CreateQuoteHeaderContainer>
			{isSubmitting ? (
				<CreateQuoteFormSkeleton />
			) : (
				<CreateQuotePageContainer data-testid="create-quote-page">
					<DesignerRequestFormContainer
						square
						elevation={1}
					>
						<DesignerRequestFormAccountAndBillToContainer>
							<FileContentsLogo isBuildOrderPage={undefined}>
								<>
									<AccountBillToLogo
										parentBrand={configuration?.globals?.brand?.code}
										className="body2"
										src={
											brandCheck(configuration?.globals?.brand?.code)
												? `/assets/manufacture_logos/${configuration?.globals?.brand?.code}.png`
												: "/assets/tandem_logos/cwg_logo.png"
										}
										alt={
											brandCheck(configuration?.globals?.brand?.code)
												? (configuration?.globals?.brand?.description ?? "")
												: "CabinetworksGroup Logo"
										}
									/>
									<SummaryProductLine
										data-testid="product-line"
										fontVariant="large"
									>
										{configuration?.globals?.productLine?.description}
									</SummaryProductLine>
								</>
							</FileContentsLogo>
							<FormControl fullWidth>
								<Controller
									name="accountNumber"
									control={control}
									render={({ field: { onChange, value = null }, fieldState: { error } }) => (
										<AutoComplete
											disableClearable
											required
											value={value}
											isLoading={false}
											label={SelectAccountNumber}
											getOptionLabel={(option) =>
												option?.accountNumber && option?.accountName
													? `${option.accountNumber} - ${option.accountName}`
													: (option?.accountNumber ?? "")
											}
											onChange={(_event, value) => {
												setValue(
													"billToNumber",
													null as unknown as QuoteRequestFields["billToNumber"]
												);
												onChange(value);
											}}
											options={sortedAccounts(accounts) ?? []}
											dataTestId="select-account-number"
											isError={!!error}
											errorText={error?.message}
										/>
									)}
								/>
							</FormControl>
							<FormControl fullWidth>
								<Controller
									name="billToNumber"
									control={control}
									render={({ field: { onChange, value = null }, fieldState: { error } }) => (
										<AutoComplete
											required
											value={value}
											disableClearable
											disabled={!selectedAccount}
											label={SelectBillToNumber}
											onChange={(_event, value) => onChange(value)}
											getOptionLabel={(option: models["BillToViewModel"]) => {
												const projectName = option.projectName?.trim()
													? option.projectName + " - "
													: "";
												const logisticsMode = option.logisticsMode
													? " - " + convertToTitleCase(option.logisticsMode)
													: "";
												return option
													? `${projectName}${option.city}, ${option.state}${logisticsMode} - ${option.billToId}`
													: "";
											}}
											options={
												sortedBillTos(
													(selectedAccount as models["CustomerAccountViewModel"] | null)
														?.billTos as models["BillToViewModel"][]
												) ?? []
											}
											dataTestId="select-bill-to-number"
											isError={!!error}
											errorText={error?.message}
										/>
									)}
								/>
							</FormControl>
						</DesignerRequestFormAccountAndBillToContainer>
						<DesignerRequestSKUContainer>
							<div data-testid="new-order-build-label">
								<DesignerCustomizationDesignerRequestSKUTitle>
									{getAttributes(configuration ?? {})}
								</DesignerCustomizationDesignerRequestSKUTitle>
								<DesignerCustomizationDesignerRequestSKUSubtitle>
									{getSubheaderAttributes(configuration ?? {})}
								</DesignerCustomizationDesignerRequestSKUSubtitle>
							</div>
							<DesignerRequestBaseSKUContainer>
								<DesignerCustomizationDesignerRequestSKUTitle>
									{BaseItemHeaderText}
								</DesignerCustomizationDesignerRequestSKUTitle>
								<RequiredFieldText>{RequiredFieldsText}</RequiredFieldText>
								{configuration && (
									<AddLineItem
										handleLineItem={onSubmit}
										configuration={configuration}
										selectedEditItem={selectedEditItem}
										isCustomQuote={true}
										isAddLineItem={false}
									/>
								)}
							</DesignerRequestBaseSKUContainer>
						</DesignerRequestSKUContainer>
					</DesignerRequestFormContainer>
					<DesignerCustomizationDetailsFormContainer
						square
						elevation={1}
					>
						<DesignerCustomizationDetailsFormHeader>
							{CustomizationDetailsHeaderText}
						</DesignerCustomizationDetailsFormHeader>
						<TopBarContainer>
							<RequiredFieldText>{RequiredFieldsText}</RequiredFieldText>

							<LinkContainer>
								<LinkTitle>{HelpfulResourcesTitle}</LinkTitle>
								<ResourceLink
									href="https://cabinetworksgroup.com/custom-quote-faqs/"
									target="_blank"
									rel="noopener noreferrer"
								>
									{GeneralFAQText}
								</ResourceLink>
								<ResourceLink
									href="https://cabinetworksgroup.com/custom-quotes-2020/"
									target="_blank"
									rel="noopener noreferrer"
								>
									{EnterQuote2020Text}
								</ResourceLink>
							</LinkContainer>
						</TopBarContainer>

						<DesignerCustomizationDetailsFormContentContainer>
							<FormControl fullWidth>
								<Controller
									name="customizationDetails"
									control={control}
									render={({ field: { onChange, value }, fieldState: { error } }) => (
										<TextField
											id="customization-details"
											label={DescribeCustomizationLabel}
											placeholder="Limit 1 customized item per request. Exceeding this limit will result in your request being declined."
											multiline
											rows={4}
											value={value || ""}
											onChange={onChange}
											error={!!error}
											slotProps={{
												inputLabel: {
													shrink: true
												}
											}}
											helperText={
												error?.message ? (
													<ErrorHelperTextContainer>
														<ErrorMessageContainer>
															<ErrorOutlineIcon
																data-testid="helper-text-error-icon"
																aria-label={FieldError}
																fontSize="small"
															/>
															{error.message}
														</ErrorMessageContainer>
														<CharacterCountError>
															( {(value || "").length}/{MAX_DETAILS_LENGTH} )
														</CharacterCountError>
													</ErrorHelperTextContainer>
												) : (
													`( ${(value || "").length}/${MAX_DETAILS_LENGTH} )`
												)
											}
											variant="outlined"
											data-testid="customization-details-field"
											FormHelperTextProps={RightAlignedFormHelperTextProps}
										/>
									)}
								/>
							</FormControl>
							<SketchUpload
								maxFiles={3}
								variant="outlined"
								onFilesSelected={handleFilesSelected}
							/>
						</DesignerCustomizationDetailsFormContentContainer>
					</DesignerCustomizationDetailsFormContainer>
					<OrderDetailsFormContainer
						square
						elevation={1}
					>
						<OrderDetailsFormHeader>{OrderDetailsHeaderText}</OrderDetailsFormHeader>
						<RequiredFieldText>{RequiredFieldsText}</RequiredFieldText>
						<DesignerInfoDesignerContainer>
							<DesignerNumberContainer>
								<FormControl>
									<Controller
										name="designer"
										control={control}
										render={({ field: { onChange, value = null }, fieldState: { error } }) => (
											<>
												<AutoComplete
													options={Array.from(
														new Set([...designerOptions, DesignerIsNotHereText])
													)}
													onChange={(_event, value) => {
														onChange(value);
														handleDesignerChange(value);
													}}
													getOptionLabel={(option: models["DesignerViewModel"] | string) => {
														if (typeof option === "string") {
															return option;
														} else {
															return `${option.firstName ? option.firstName + " " : ""}${option.lastName ? option.lastName + " " : ""}${option.firstName || option.lastName ? "- " : ""}${option.designerNumber}`;
														}
													}}
													filterOptions={designerOptionsWithCustomDesignerText}
													value={isCustomDesigner ? DesignerIsNotHereText : value}
													isError={!!error}
													errorText={error?.message}
													label={SelectDesignerNumberLabel}
													dataTestId="shipping-order-details-designer"
													isLoading={false}
												/>
												{!isCustomDesigner && (
													<FormHelperText>
														{DesignerNotFoundContactSalesRepText}
													</FormHelperText>
												)}
											</>
										)}
									/>
								</FormControl>
								{(designer === DesignerIsNotHereText || isCustomDesigner) && (
									<FormControl>
										<Controller
											name="customDesigner"
											control={control}
											render={({ field: { onChange, value }, fieldState: { error } }) => (
												<TextField
													id="customDesigner"
													label={EnterDesignerNumberLabel}
													size="small"
													value={value || ""}
													onChange={onChange}
													error={!!error}
													helperText={
														error?.message ? (
															<SimpleErrorContainer>
																<ErrorOutlineIcon
																	data-testid="helper-text-error-icon"
																	aria-label={FieldError}
																	fontSize="small"
																/>
																{error.message}
															</SimpleErrorContainer>
														) : (
															DesignerNumberSixDigitsStartWithOneText
														)
													}
													data-testid="custom-designer-input"
												/>
											)}
										/>
									</FormControl>
								)}
							</DesignerNumberContainer>
							<FormControl fullWidth>
								<Controller
									name="customItemName"
									control={control}
									render={({ field: { onChange, value }, fieldState: { error } }) => (
										<TextField
											id="custom-item-name"
											label={CustomItemNameLabel}
											size="small"
											multiline
											rows={1}
											value={value || ""}
											onChange={onChange}
											error={!!error}
											helperText={
												error?.message ? (
													<ErrorHelperTextContainer>
														<ErrorMessageContainer>
															<ErrorOutlineIcon
																data-testid="helper-text-error-icon"
																aria-label={FieldError}
																fontSize="small"
															/>
															{error.message}
														</ErrorMessageContainer>
														<CharacterCountError>
															( {(value || "").length}/{MAX_ITEM_NAME_LENGTH} )
														</CharacterCountError>
													</ErrorHelperTextContainer>
												) : (
													`( ${(value || "").length}/${MAX_ITEM_NAME_LENGTH} )`
												)
											}
											variant="outlined"
											data-testid="custom-item-name-field"
											FormHelperTextProps={RightAlignedFormHelperTextProps}
										/>
									)}
								/>
							</FormControl>
						</DesignerInfoDesignerContainer>
					</OrderDetailsFormContainer>
					{!isSubmitting && (
						<SubmitButtonContainer>
							<Button
								type="button"
								variant="contained"
								color="primary"
								size="large"
								data-testid="submit-quote-request-button"
								onClick={async () => {
									const parentFormValid = await trigger();
									if (parentFormValid) {
										const formElement = document.getElementById(
											"add-line-item-form"
										) as HTMLFormElement;
										if (formElement) {
											formElement.requestSubmit();
										}
									}
								}}
							>
								{SubmitCustomQuoteRequestButtonText}
							</Button>
						</SubmitButtonContainer>
					)}
				</CreateQuotePageContainer>
			)}
		</Page>
	);
};

export default CreateQuotePage;
